import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, FilePlus, MessageCircle, Pencil, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, docUrl, errMsg } from "@/lib/api";
import { DOC_CATEGORIES, DOC_STATUS, DOC_TONE, fmtDate, fmtEuro } from "@/lib/format";
import { Btn, Pill, Spinner } from "@/components/rp";
import { CantiereHero } from "@/components/CantiereHero";
import { PhaseList } from "@/components/PhaseList";
import { PhotoGrid } from "@/components/PhotoGrid";
import { CantiereFormDialog, PhaseDialog, UploadDocDialog, UploadPhotoDialog } from "@/components/admin/dialogs";

const catLabel = (v) => DOC_CATEGORIES.find((c) => c.v === v)?.l.replace(/i$/, "o") || v;

const DocList = ({ docs }) => (
  <div className="space-y-2" data-testid="admin-documents-list">
    {!docs.length && <p className="text-sm text-[#757575]">Nessun documento caricato.</p>}
    {docs.map((d) => (
      <a key={d.id} href={docUrl(d)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#EBEBEB] hover:bg-[#F7F7F7]" data-testid={`admin-document-${d.id}`}>
        <FileText className="h-5 w-5 shrink-0" strokeWidth={1.5} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium truncate">{d.title}</span>
          <span className="block text-xs text-[#757575]">{catLabel(d.category)} · {fmtDate(d.created_at)}{d.amount != null && ` · ${fmtEuro(d.amount)}`}{d.client_note && ` · “${d.client_note}”`}</span>
        </span>
        {d.status !== "none" && <Pill tone={DOC_TONE[d.status]}>{DOC_STATUS[d.status]}</Pill>}
        <ExternalLink className="h-4 w-4 text-[#757575]" />
      </a>
    ))}
  </div>
);

export default function AdminCantiereDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [phase, setPhase] = useState(null);
  const [dialog, setDialog] = useState(null);

  const load = () => api.get(`/cantieri/${id}`).then((r) => setC(r.data)).catch(() => navigate("/admin"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  const merge = (data) => setC((prev) => ({ ...prev, ...data, photos: prev.photos, documents: prev.documents, client_name: prev.client_name }));

  const toggleTask = async (t) => {
    try { merge((await api.patch(`/cantieri/${id}/tasks/${t.id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!c) return <Spinner />;
  return (
    <div className="space-y-8">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#757575] hover:text-black" data-testid="back-to-cantieri"><ArrowLeft className="h-4 w-4" /> Cantieri</Link>
      <CantiereHero c={c} photosCount={c.photos.length}>
        <Btn onClick={() => navigate(`/admin/messaggi/${c.client_id}`)} data-testid="write-client-button"><MessageCircle className="h-4 w-4" /> Scrivi al cliente</Btn>
        <Btn variant="secondary" onClick={() => setDialog("edit")} data-testid="edit-cantiere-button"><Pencil className="h-4 w-4" /> Modifica</Btn>
        <Btn variant="secondary" onClick={() => setDialog("photo")} data-testid="upload-photo-button"><Camera className="h-4 w-4" /> Carica foto</Btn>
        <Btn variant="secondary" onClick={() => setDialog("doc")} data-testid="upload-doc-button"><FilePlus className="h-4 w-4" /> Carica documento</Btn>
      </CantiereHero>
      <p className="text-sm text-[#757575]">Cliente: <Link to={`/admin/clienti/${c.client_id}`} className="text-black font-medium hover:underline" data-testid="cantiere-client-link">{c.client_name}</Link></p>
      <div className="grid min-[900px]:grid-cols-[1.1fr_1fr] gap-8">
        <section className="space-y-6">
          {c.description && <div><p className="rp-label mb-2">Descrizione</p><p className="leading-relaxed text-[15px]">{c.description}</p></div>}
          <div>
            <p className="rp-label mb-3">Fasi · clicca un'attività per spuntarla</p>
            <PhaseList phases={c.phases} admin onToggleTask={toggleTask} onManage={setPhase} />
          </div>
        </section>
        <section className="space-y-8">
          <div><p className="rp-label mb-3">Documenti</p><DocList docs={c.documents} /></div>
          <div><p className="rp-label mb-3">Foto</p><PhotoGrid photos={c.photos} phases={c.phases} /></div>
        </section>
      </div>
      {phase && <PhaseDialog cantiere={c} phase={phase} onClose={() => setPhase(null)} onSaved={merge} />}
      <CantiereFormDialog open={dialog === "edit"} onClose={() => setDialog(null)} cantiere={c} onSaved={merge} />
      <UploadPhotoDialog open={dialog === "photo"} onClose={() => setDialog(null)} cantiere={c} onSaved={load} />
      <UploadDocDialog open={dialog === "doc"} onClose={() => setDialog(null)} cantiere={c} onSaved={load} />
    </div>
  );
}
